import { LoaderManagerService } from '@anecdotes/design-system/loaders';
import { ModalWindowService } from '@anecdotes/design-system/modals';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  SkipSelf,
  inject,
  DestroyRef,
  ViewChildren,
  QueryList,
  ElementRef,
} from '@angular/core';
import { Observable, firstValueFrom, shareReplay } from 'rxjs';
import { TrustCenterFacadeService, RequirementsCategoryResponse, RequirementResponse, TrustResourceEventDataProperty, TrustCenterEventService } from '@anecdotes/shared-trust-center';
import { ControlEditSidePanelComponent } from '../control-edit-side-panel/control-edit-side-panel.component';
import { ControlSidePanelComponent } from '../control-side-panel/control-side-panel.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const ELEMENTS_PER_ROW = 3;

@Component({
  selector: 'app-control-summary',
  templateUrl: './control-summary.component.html',
  styleUrls: ['./control-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ControlSummaryComponent implements OnInit, OnDestroy, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @ViewChildren('controlsLabel', { read: ElementRef })
  private controlLabels: QueryList<ElementRef<HTMLElement>>;

  @Input()
  control: RequirementsCategoryResponse;

  editing: boolean;

  requirementsVisibleCount: number;

  grantedAccess$: Observable<boolean>;

  editing$: Observable<boolean>;

  isPreviewMode$: Observable<boolean>;

  readonly requirementLabelMapping: Record<string, string> = {
    '=1': this.buildTranslationKey('control.requirementLower'),
    other: this.buildTranslationKey('control.requirementsLower'),
  };

  get requirementsToShow(): RequirementResponse[] {
    const requirements = this.control.requirements
      .sort((req1, req2) => req1.name.localeCompare(req2.name))
      .filter((requirement) => requirement.is_visible);

    return requirements.slice(0, ELEMENTS_PER_ROW);
  }

  get maxControlsLabelWidth(): number | undefined {
    if (!this.controlLabels) {
      return;
    }

    return Math.max(...this.controlLabels.map((label) => label.nativeElement.offsetWidth));
  }

  constructor(
    @SkipSelf() private readonly loaderManager: LoaderManagerService,
    private readonly modalWindowService: ModalWindowService,
    private readonly trustCenterFacadeService: TrustCenterFacadeService,
    private readonly cd: ChangeDetectorRef,
    private readonly trustCenterEventService: TrustCenterEventService,
  ) {}

  ngOnInit(): void {
    this.editing$ = this.trustCenterFacadeService.isEditing();
    this.isPreviewMode$ = this.trustCenterFacadeService.isPreviewMode()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.grantedAccess$ = this.trustCenterFacadeService.isLoggedIn();

    this.requirementsVisibleCount = this.control.requirements.filter((requirement) => requirement.is_visible).length;

    this.trustCenterFacadeService
      .isEditing()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((editing) => {
        this.editing = editing;
        this.cd.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.loaderManager.hide();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      this.requirementsVisibleCount = this.control.requirements.filter((requirement) => requirement.is_visible).length;
      this.cd.detectChanges();
    }
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  trackRequirementBy(_: number, requirement: RequirementResponse): string {
    return requirement.id;
  }

  openEditSidePanel(): void {
    this.modalWindowService.openMediumSlidePanel({
      componentsToSwitch: [
        {
          id: 'control-edit-side-panel',
          componentType: ControlEditSidePanelComponent,
          contextData: {
            control: this.control,
          },
        },
      ],
      options: {
        closeOnEsc: true,
      },
    });
  }

  openViewSidePanel(): void {
    const componentsToSwitch = [
      {
        id: 'app-control-side-panel',
        componentType: ControlSidePanelComponent,
        contextData: {
          control: this.control,
        },
      },
    ];

    this.modalWindowService.openMediumSlidePanel({
      id: 'control-side-panel',
      componentsToSwitch,
      options: {
        closeOnEsc: true,
      },
    });
  }

  getNumberOfControls(requirement: RequirementResponse): number {
    return Object.values(requirement.linked_frameworks).reduce((total, controls) => total + controls.length, 0);
  }

  async openSidePanel(event: MouseEvent): Promise<void> {
    const targetElement = event.target as HTMLElement;
    const currentTargetElement = event.currentTarget as HTMLElement;
    const isClickedOutside = !currentTargetElement.contains(targetElement);

    if (isClickedOutside) {
      return;
    }

    const editing = await firstValueFrom(this.editing$);
    const isPreviewMode = await firstValueFrom(this.isPreviewMode$);

    if (editing) {
      this.openEditSidePanel();
    } else {
      if (!isPreviewMode) {
        const companyName = await firstValueFrom(this.trustCenterFacadeService.getName());
        this.trustCenterEventService.trackClickOnResourceItem(TrustResourceEventDataProperty.Requirements, this.control.name, companyName);
      }

      this.openViewSidePanel();
    }
  }
}
